/**
 * AWAC booking-confirm fallback.
 * ---------------------------------------------------------------------------
 * The Apex action (BookVisitAwacService) always returns a deterministic
 * BookingConfirm envelope, and the agent is instructed to relay it VERBATIM.
 * In practice the LLM sometimes paraphrases and replies with only the
 * envelope's `text` field ("You're all set! Your visit ... is booked for
 * 2026-06-13 at 14:00.") — no JSON, so no card, no chip, no confetti.
 *
 * This module makes the confirmation deterministic on the CLIENT side:
 *
 *   1. When the visitor taps a slot, BookingSlots calls rememberSlotPick()
 *      with the picked date/time + the picker's attributes.
 *   2. When the NEXT agent message arrives as plain text (no envelope), and
 *      it clearly confirms THAT slot (contains the picked date AND time AND
 *      a positive keyword, and no negative keyword), the message text is
 *      replaced with a locally synthesized BookingConfirm envelope before
 *      rendering — so the card, header chip, analytics, and confetti all
 *      behave exactly as if the agent had relayed the envelope.
 *
 * Matching is deliberately conservative: the known Apex conflict/apology
 * texts ("Sorry — that slot was just taken", "Something went wrong while
 * booking", FR "vient tout juste d'être réservée", "Un imprévu est survenu")
 * all contain negative keywords and are checked FIRST, so they can never be
 * misread as a confirmation. When in doubt, do nothing — a plain text bubble
 * is always a safe fallback.
 */

import { getAwacState, setAwacState, clearAwacState } from "./awacSessionState";
import { getAwacEnvelope, getStaticContentPayload, isConversationEntryStaticContentMessage, isMessageFromEndUser } from "../helpers/conversationEntryUtil";
import { CONVERSATION_CONSTANTS } from "../helpers/constants";
import { track, AwacEvents } from "./analytics";

const PENDING_KEY = "pendingSlotPick";
/** A pick older than this is stale — never synthesize from it. */
const PENDING_TTL_MS = 10 * 60 * 1000;

/**
 * NEGATIVE markers — checked FIRST. Any hit means "this is not a successful
 * confirmation" (conflict, error, apology) and blocks synthesis.
 * Compared against a lowercased, accent-stripped copy of the text.
 */
const NEGATIVE_MARKERS = [
    "sorry", "taken", "wrong", "unable", "cannot", "can't", "no longer",
    "not available", "unavailable",
    "desole", "imprevu", "tout juste", "impossible", "n'est plus",
    "plus disponible", "indisponible", "erreur"
];

/** POSITIVE markers — at least one required (accent-stripped, lowercase). */
const POSITIVE_MARKERS = [
    "booked", "confirmed", "all set", "scheduled",
    "reserve", "reservee", "confirme", "confirmee", "c'est confirme",
    "est planifie"
];

/** Lowercase + strip diacritics so FR accents never break matching. */
function normalize(text) {
    try {
        return text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    } catch (e) {
        return text.toLowerCase();
    }
}

/** "2026-06-13" -> ["2026-06-13", "june 13", "13 juin"] (normalized). */
function dateForms(dateStr) {
    const forms = [dateStr];
    const [y, m, d] = (dateStr || "").split("-").map(Number);
    if (y && m && d) {
        const dt = new Date(y, m - 1, d); // local construction — no TZ drift
        try {
            forms.push(dt.toLocaleDateString("en-CA", { month: "long", day: "numeric" }));
            forms.push(dt.toLocaleDateString("fr-CA", { day: "numeric", month: "long" }));
        } catch (e) {
            // toLocaleDateString unavailable — ISO form alone is fine.
        }
    }
    return forms.map(normalize);
}

// ===== pending pick lifecycle ===============================================

/** Called by BookingSlots when the visitor taps a slot. */
export function rememberSlotPick(pick) {
    if (!pick || !pick.date || !pick.time) return;
    setAwacState(PENDING_KEY, { ...pick, ts: Date.now() });
}

export function clearSlotPick() {
    clearAwacState(PENDING_KEY);
}

/** The pending pick, or undefined when absent/stale. */
export function getSlotPick() {
    const p = getAwacState(PENDING_KEY);
    if (!p || !p.date || !p.time) return undefined;
    if (Date.now() - (Number(p.ts) || 0) > PENDING_TTL_MS) {
        clearSlotPick();
        return undefined;
    }
    return p;
}

// ===== synthesis ============================================================

/** Builds a BookingConfirm envelope matching Apex's buildConfirmEnvelope shape. */
function synthesizeConfirmEnvelope(text, pick) {
    return {
        awac: 1,
        text: text,
        template: [{ name: "BookingConfirm" }],
        options: {
            booking: {
                date: pick.date,
                time: pick.time,
                durationMin: Number(pick.durationMin) || 60,
                timezone: pick.timezone || "America/Toronto",
                projectName: pick.projectName || "",
                name: pick.name || "",
                email: pick.email || "",
                locale: pick.locale === "fr" ? "fr" : "en"
            }
        },
        synthesized: true
    };
}

/**
 * Inspect a freshly received agent message and, when the agent paraphrased
 * the booking confirmation, replace the message text with a synthesized
 * BookingConfirm envelope. Mutates the entry in place BEFORE it is added to
 * the conversation, so every downstream consumer sees a normal envelope.
 *
 * @param {object} conversationEntry - the new (non end-user) entry.
 */
export function applyBookingConfirmFallback(conversationEntry) {
    try {
        if (!conversationEntry || isMessageFromEndUser(conversationEntry)) return;
        if (!isConversationEntryStaticContentMessage(conversationEntry)) return;

        // A real envelope arrived (confirm, refreshed picker, anything):
        // Apex answered deterministically — the pending pick is settled.
        if (getAwacEnvelope(conversationEntry)) {
            clearSlotPick();
            return;
        }

        const pick = getSlotPick();
        if (!pick) return;

        const payload = getStaticContentPayload(conversationEntry);
        if (!payload || payload.formatType !== CONVERSATION_CONSTANTS.FormatTypes.TEXT) return;
        const text = typeof payload.text === "string" ? payload.text : "";
        if (!text) return;

        const norm = normalize(text);

        // Negative markers FIRST — conflict/apology texts must never match.
        for (const marker of NEGATIVE_MARKERS) {
            if (norm.indexOf(marker) !== -1) {
                clearSlotPick(); // the booking attempt is settled (failed)
                return;
            }
        }

        // Require the picked time, the picked date, and a positive marker.
        if (norm.indexOf(pick.time) === -1) return;
        if (!dateForms(pick.date).some((f) => norm.indexOf(f) !== -1)) return;
        if (!POSITIVE_MARKERS.some((m) => norm.indexOf(m) !== -1)) return;

        // Synthesize: rewrite the entry's text into the envelope JSON.
        payload.text = JSON.stringify(synthesizeConfirmEnvelope(text, pick));
        clearSlotPick();
        track(AwacEvents.CONFIRM_SYNTHESIZED, { date: pick.date, time: pick.time });
    } catch (e) {
        // The fallback must never break message handling.
        // eslint-disable-next-line no-console
        console.warn("[AWAC] Booking-confirm fallback skipped:", e);
    }
}
